import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { TokenService } from '@/services/tokenService';
import { Eye, EyeOff, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const getTokenFromUrl = (searchParams: URLSearchParams, location: any): string | null => {
  let token = searchParams.get('token');
  if (token) return token;

  const windowParams = new URLSearchParams(window.location.search);
  token = windowParams.get('token');
  if (token) return token;

  if (window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    token = hashParams.get('token');
    if (token) return token;
  }

  if (location.search) {
    const match = location.search.match(/[?&]token=([^&]+)/);
    if (match) return match[1];
  }

  return null;
};

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [email, setEmail] = useState<string>('');
  const [token, setToken] = useState<string | null>(null);
  const [tokenRecord, setTokenRecord] = useState<any>(null);
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const tokenFromUrl = getTokenFromUrl(searchParams, location);
    
    if (!tokenFromUrl) {
      setTokenValid(false);
      toast({
        title: "Invalid Link",
        description: "The reset password link is invalid or has expired.",
        variant: "destructive",
      });
      setTimeout(() => navigate('/auth'), 3000);
      return;
    }

    setToken(tokenFromUrl);

    const validateToken = async () => {
      try {
        const result = await TokenService.validatePasswordResetToken(tokenFromUrl);
        
        if (result.valid && result.email) {
          setTokenValid(true);
          setEmail(result.email);
          setTokenRecord(result.record);
        } else {
          setTokenValid(false);
          toast({
            title: "Invalid Link",
            description: result.error || "The reset password link is invalid or has expired.",
            variant: "destructive",
          });
          setTimeout(() => navigate('/auth'), 3000);
        }
      } catch (error) {
        setTokenValid(false);
        toast({
          title: "Error",
          description: "Failed to validate reset link",
          variant: "destructive",
        });
        setTimeout(() => navigate('/auth'), 3000);
      }
    };

    validateToken();
  }, [searchParams, location, navigate, toast]);

  useEffect(() => {
    if (formData.password) {
      setPasswordStrength({
        hasMinLength: formData.password.length >= 8,
        hasUpperCase: /[A-Z]/.test(formData.password),
        hasLowerCase: /[a-z]/.test(formData.password),
        hasNumber: /[0-9]/.test(formData.password),
        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
      });
    }
  }, [formData.password]);

  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(v => v === true);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      toast({
        title: "Error",
        description: "Reset token not found",
        variant: "destructive",
      });
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      toast({
        title: "Missing Information",
        description: "Please enter and confirm your new password",
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (!isPasswordStrong()) {
      toast({
        title: "Weak Password",
        description: "Please meet all password requirements",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('reset_user_password', {
        user_email: email,
        new_password: formData.password,
        reset_token: token
      });

      if (error) {
        throw new Error(error.message || 'Failed to reset password');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to reset password');
      }

      toast({
        title: "Password Reset Successful",
        description: "Your password has been updated successfully. Please sign in with your new password.",
      });

      setFormData({ password: '', confirmPassword: '' });
      setTimeout(() => navigate('/auth'), 2000);
      
    } catch (error) {
      toast({
        title: "Reset Failed",
        description: error instanceof Error ? error.message : "Failed to reset password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const PasswordStrengthIndicator = () => (
    <div className="space-y-2 mt-2 text-xs">
      <p className="font-semibold text-gray-700">Password requirements:</p>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {passwordStrength.hasMinLength ? (
            <CheckCircle2 className="h-3 w-3 text-green-600" />
          ) : (
            <XCircle className="h-3 w-3 text-gray-400" />
          )}
          <span className={passwordStrength.hasMinLength ? "text-green-600" : "text-gray-600"}>
            At least 8 characters
          </span>
        </div>
        <div className="flex items-center gap-2">
          {passwordStrength.hasUpperCase ? (
            <CheckCircle2 className="h-3 w-3 text-green-600" />
          ) : (
            <XCircle className="h-3 w-3 text-gray-400" />
          )}
          <span className={passwordStrength.hasUpperCase ? "text-green-600" : "text-gray-600"}>
            One uppercase letter
          </span>
        </div>
        <div className="flex items-center gap-2">
          {passwordStrength.hasLowerCase ? (
            <CheckCircle2 className="h-3 w-3 text-green-600" />
          ) : (
            <XCircle className="h-3 w-3 text-gray-400" />
          )}
          <span className={passwordStrength.hasLowerCase ? "text-green-600" : "text-gray-600"}>
            One lowercase letter
          </span>
        </div>
        <div className="flex items-center gap-2">
          {passwordStrength.hasNumber ? (
            <CheckCircle2 className="h-3 w-3 text-green-600" />
          ) : (
            <XCircle className="h-3 w-3 text-gray-400" />
          )}
          <span className={passwordStrength.hasNumber ? "text-green-600" : "text-gray-600"}>
            One number
          </span>
        </div>
        <div className="flex items-center gap-2">
          {passwordStrength.hasSpecialChar ? (
            <CheckCircle2 className="h-3 w-3 text-green-600" />
          ) : (
            <XCircle className="h-3 w-3 text-gray-400" />
          )}
          <span className={passwordStrength.hasSpecialChar ? "text-green-600" : "text-gray-600"}>
            One special character (!@#$%^&*...)
          </span>
        </div>
      </div>
    </div>
  );

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Invalid or expired reset link. Redirecting to login page...
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-green-600 p-3 rounded-full">
              <img src="/greencard-uploads/logo.png" alt="GreenCard Logo" className="h-8 w-8 rounded-full bg-white object-contain" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Reset Password</h1>
          <p className="text-gray-600 mt-2">
            Reset password for: <span className="font-semibold">{email}</span>
          </p>
          {tokenRecord && (
            <p className="text-xs text-gray-500 mt-1">
              Token expires: {new Date(tokenRecord.expires_at).toLocaleString()}
            </p>
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter new password"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {formData.password && <PasswordStrengthIndicator />}
              </div>

              <div>
                <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmNewPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm new password"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !isPasswordStrong() || formData.password !== formData.confirmPassword}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              <Button
                type="button"
                variant="link"
                className="w-full"
                onClick={() => navigate('/auth')}
              >
                Back to Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};